import {
	Checkbox,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as ExpandMoreRoundedIcon } from 'common/assets/images/select-chevron.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import ModalEle from 'common/components/Modal/ModalEle';
import { NO_DATA_FOUND } from 'common/constants';
import { PRC_BUTTON_TEXT } from 'common/constants/TaxonomyConstants';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IListOfMetaData } from 'features/RiskProfile/api/type-def';

import { IMetaDataEditModal } from '../api/type-def';

import styles from './MetaDataEditModal.module.scss';

const { CANCEL, UPDATE } = PRC_BUTTON_TEXT;

const MetaDataEditModal = (props: IMetaDataEditModal) => {
	const {
		isModalOpen,
		modalCloseHandler,
		editMetaData,
		metaDataArray,
		selectedMetaDataOption,
		isMetaDataListLoading,
		handleEditMetaDataChange,
		isMultiSelect = false,
		updateMetaDataHandler,
		disableUpdateBtn,
	} = props;

	const { iconFill, iconWidth, iconSpacing, iconHeight } = styles;

	const selectStyle = {
		'& .MuiSelect-select.MuiInputBase-input.MuiFilledInput-input:focus': {
			backgroundColor: 'transparent',
			width: '100%',
		},
		'& .MuiSelect-icon': {
			width: iconWidth,
			height: iconHeight,
			marginRight: iconSpacing,
			fill: iconFill,
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: 'transparent',
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: 'transparent',
		},
		'.MuiInputBase-root': {
			border: 'none',
		},
		'.MuiOutlinedInput-notchedOutline': {
			border: 'none',
		},
	};

	const renderRow = (
		metaData: IListOfMetaData,
		isMultiSelect: boolean,
		selectedMetaDataOption: IListOfMetaData[],
	) => (
		<MenuItem
			key={metaData?.value}
			value={metaData?.value}
			sx={{
				backgroundColor: 'transparent !important',
				'&.Mui-selected:hover': {
					backgroundColor: 'transparent !important',
				},
			}}
		>
			{isMultiSelect && (
				<Checkbox
					checked={selectedMetaDataOption?.some(
						(selectedItem) => selectedItem?.value === metaData?.value,
					)}
				/>
			)}

			<Tooltip title={metaData?.displayValue}>
				<ListItemText primary={metaData?.displayValue} />
			</Tooltip>
		</MenuItem>
	);

	return (
		<ModalEle
			open={isModalOpen}
			handleClose={modalCloseHandler}
			restrictDefaultCloseAction
			sxBoxProps={{
				width: '35%',
				margin: 'auto',
			}}
		>
			<Stack
				className={styles['data-container']}
				onMouseDown={(event) => event.stopPropagation()}
				onClick={(event) => event.stopPropagation()}
				data-testid="modal-container"
			>
				<Typography className={styles['modal-title']}>
					{`${toSentenceCase('Edit')} ${editMetaData?.toLowerCase()}`}
				</Typography>
				<CloseIcon
					fontSize="medium"
					onClick={modalCloseHandler}
					className={`${styles['add-closemodal']} ${
						isMetaDataListLoading ? styles['disable-btn'] : ''
					}`}
					data-testid="close-icon"
				/>
			</Stack>
			{isMetaDataListLoading ? (
				<Loader show className={styles['loader-style']} />
			) : (
				<div>
					{metaDataArray?.length > 0 ? (
						<>
							<Typography className={styles['modal-subtitle']}>
								{toSentenceCase(`Select the desired ${editMetaData} below.`)}
							</Typography>
							<div>
								<FormControl className={styles['meta-data-formcontrol']}>
									<InputLabel className={styles['input-label']}>
										{editMetaData}
									</InputLabel>

									<Select
										multiple={isMultiSelect}
										value={selectedMetaDataOption?.map((item) => item?.value)}
										onChange={(e) => handleEditMetaDataChange(e, isMultiSelect)}
										IconComponent={ExpandMoreRoundedIcon}
										sx={selectStyle}
										renderValue={() =>
											selectedMetaDataOption
												?.map((item) => item?.displayValue)
												.join(', ')
										}
										data-testid="dropdown-meta-data"
										MenuProps={{
											PaperProps: {
												style: {
													maxHeight: 200,
													overflowY: 'auto',
												},
											},
										}}
									>
										{metaDataArray?.map((metaData: IListOfMetaData) =>
											renderRow(
												metaData,
												isMultiSelect,
												selectedMetaDataOption,
											),
										)}
									</Select>
								</FormControl>
							</div>
						</>
					) : (
						<Typography
							className={styles['no-data-found-msg']}
							data-testid="no-data-found-msg"
						>
							{toSentenceCase(NO_DATA_FOUND)}
						</Typography>
					)}
				</div>
			)}
			<Stack className={styles['button-container']}>
				<ButtonComp
					btnType="secondary"
					clickHandler={modalCloseHandler}
					data-testid="cancel-btn"
					disabled={isMetaDataListLoading}
				>
					{toSentenceCase(CANCEL)}
				</ButtonComp>
				<ButtonComp
					disabled={disableUpdateBtn}
					clickHandler={() => updateMetaDataHandler(selectedMetaDataOption)}
					btnType="primary"
					data-testid="update-btn"
				>
					{toSentenceCase(UPDATE)}
				</ButtonComp>
			</Stack>
		</ModalEle>
	);
};

export default MetaDataEditModal;
